import classNames from "classnames";
import React from "react";
import { getCityStateCountry } from "shared/utils/addressUtils";
import * as moment from "moment";
import ShowDateTime from "show/components/ShowDateTime";
import { AppointmentPhase } from "components";
import { PhosphorWarning } from "components/Atoms/PhosphorIcon";

interface ActiveShowCardProps {
  isHovered?: boolean
  onHover?: () => void
  onHoverEnd?: () => void
  show: server.dto.Show
  clause?: server.dto.ExclusivityClause
  isSelected: boolean
  className?: string
  onClick?: () => void
}

export const ActiveShowCard = (props: ActiveShowCardProps) =>
  <ShowCard
    isHovered={props.isHovered}
    onHover={props.onHover}
    onHoverEnd={props.onHoverEnd}
    showId={props.show.showId}
    eventName={props.show.eventName}
    venueName={props.show.venue?.name}
    venueCityCountry={props.show.venue ? getCityStateCountry(props.show.venue.address) : ""}
    clause={props.clause}
    appointmentPhase={props.show.status.appointmentPhase}
    isSelected={props.isSelected}
    dateTime={props.show.dateTime}
    className={props.className}
    onClick={props.onClick}
  />

interface ConflictingShowCardProps {
  isHovered?: boolean
  onHover?: () => void
  onHoverEnd?: () => void
  show: server.dto.ConflictingShow
  clause?: server.dto.ExclusivityClause
  isSelected: boolean
  compareToShowDate: moment.Moment
  className?: string
  onClick?: () => void
}

export const ConflictingShowCard = (props: ConflictingShowCardProps) =>
  <ShowCard
    isHovered={props.isHovered}
    onHover={props.onHover}
    onHoverEnd={props.onHoverEnd}
    showId={props.show.showId}
    eventName={props.show.eventName}
    venueName={props.show.venue?.name}
    venueCityCountry={props.show.venue ? getCityStateCountry(props.show.venue.address) : ""}
    clause={props.clause}
    distance={props.show.distance}
    unitOfDistance={props.show.unitOfDistance}
    compareToShowDate={props.compareToShowDate}
    appointmentPhase={props.show.appointmentPhase}
    isSelected={props.isSelected}
    dateTime={props.show.dateTime}
    className={props.className}
    onClick={props.onClick}
  />

interface ShowCardProps {
  isHovered?: boolean
  onHover?: () => void
  onHoverEnd?: () => void
  showId: number
  eventName: string
  venueName: string
  venueCityCountry: string
  clause?: server.dto.ExclusivityClause
  distance?: number
  unitOfDistance?: server.dto.UnitsOfDistance
  isSelected?: boolean
  dateTime: server.dto.AppointmentDateTime
  compareToShowDate?: moment.Moment
  appointmentPhase: server.dto.AppointmentPhases
  className?: string
  onClick?: () => void
}

const ShowCard = (props: ShowCardProps) => {
  const {
    showId,
    eventName,
    venueName,
    venueCityCountry,
    clause,
    distance,
    unitOfDistance,
    isSelected,
    dateTime,
    compareToShowDate,
    appointmentPhase,
    className,
    onClick,
  } = props;

  const cardClass = classNames("padding-larger radius-big", className, {
    "gray-100 hoverable": !isSelected,
    "blue-bg white-text": isSelected
  });

  const hasVenue = venueName != null && venueCityCountry != null;
  return <div
    style={{ backgroundColor: props.isHovered ? "var(--hover-color)" : undefined }}
    onMouseOver={props.onHover}
    onMouseLeave={props.onHoverEnd}
    className={cardClass}
    onClick={onClick}
  >
    <div className="flex gap">
      <AppointmentPhase value={appointmentPhase} displayLabel={false} />

      <div className="grow1">
        <div className="margin-bottom-small">
          <h3>{eventName ?? "…"}</h3>
        </div>
        {hasVenue && <div className="margin-bottom-small">
          {venueName},&nbsp;{venueCityCountry}
        </div>}
        <div className="margin-bottom-small">
          <ShowDateTime dateTime={dateTime} />
        </div>
      </div>

      <div style={{ textAlign: "right" }} className="no-wrap">
        {getDiff(moment.utc(dateTime.startDisplay), compareToShowDate)}
        {getDistance(distance, unitOfDistance)}
      </div>
    </div>

    {clause && <div className="s1_red margin-top-small">
      {getClause(showId, clause, isSelected)}
    </div>}
  </div>
}

const getDiff = (showDate: moment.Moment, compareToShowDate?: moment.Moment) => {
  if (compareToShowDate == null) return null;

  const diffDays = showDate.diff(compareToShowDate, "days");

  if (diffDays === -1) {
    return <div>1 day before</div>;
  } else if (diffDays < -1) {
    return <div>{Math.abs(diffDays)} days before</div>;
  } else if (diffDays === 0) {
    return <div>same day</div>;
  } else if (diffDays === 1) {
    return <div>1 day after</div>;
  } else {
    return <div>{diffDays} days after</div>;
  }
};

const getDistance = (distance?: number, unitOfDistance?: server.dto.UnitsOfDistance) => {
  if (distance == null || distance === 0) return null;

  return <div>
    {unitOfDistance === "metric" ? `${distance} km` : `${distance} mi.`}
  </div>;
};

const getClause = (showId: number, clause: server.dto.ExclusivityClause, isSelected: boolean) => {
  return <div className={classNames("flex gap align-center", {
    "white-text": isSelected,
  })} key={showId}>
    <PhosphorWarning />
    <span>
      {RESX.Exclusivity.resxExclusivity}: {`${clause.radius} ${clause.unitOfDistance === "metric"
        ? "km"
        : "mi"}`} radius, -{clause.daysBefore} / +{clause.daysAfter} days
    </span>
  </div>;
};
